import MainPageLayout from "pages/layouts/MainPage";
import React, { useEffect, useMemo, useState } from "react";
import ImageCard from "components/Cards/ImageCard";
import Button from "components/Button";
import ProjectDescription from "./ProjectDescription";
import MainTabs from "components/UI/organisms/MainTabs";
import ProjectOverview from "./ProjectOverview";
import ProjectFiles from "./ProjectFiles";
import ProjectHistory from "./ProjectHistory";
import ProjectTabDescription from "./ProjectTabDescription";
import Discussions from "components/UI/organisms/Discussions";
import Card from "components/Cards/Card";
import { COMPONENT_TYPES } from "helpers/utils/constants";
import { CheckCircle } from "@mui/icons-material";
import ProjectAssessModal from "./ProjectAssessModal";
import ProjectSubmissionModal from "./ProjectSubmissionModal";
import useProjects from "store/projects/service-hook";
import useRedirect from "helpers/hooks/useRedirect";
import ProjectAssessmentAIModal from "./ProjectAssessmentAIModal";
import ShareModal from "components/UI/organisms/ShareModal";
import moment from "moment";
import ProjectManageOptions from "./ProjectManageOptions";
import ProjectAssessmentDetailsModal from "../../report/challenge/components/ProjectAssessmentDetailsModal";
import { NotLoggedInTabs } from "../../organization";
import ProjectCTA from "./ProjectCTA";
import { getRequirementTypesWithTab, isRequirementCompleted } from "../utils";
import { useSelector } from "react-redux";
import { REDUCER_TYPES } from "store";
import ConfirmationModal from "components/Modal/ConfirmationModal";

const project_tabs = [
  "Description",
  "Project Details",
  "Files",
  "Discussion",
  "History",
];

const ProjectsMainPage = ({ project, setProject, t = (d) => d }) => {
  // STATE HOOKS FOR VARIOUS MODALS, FLAGS, AND CONTROLS
  const [tab, setTab] = useState(0);
  const [history, setHistory] = useState(false);
  const [assessmentOpen, setAssessmentOpen] = useState(false);
  const [assessmentAIOpen, setAssessmentAIOpen] = useState(false);
  const [submissionOpen, setSubmissionOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const [lateSubmissionMessage, setLateSubmissionMessage] = useState("");
  const [openConfirmationModal, setOpenConfirmationModal] = useState(false);

  // DUE DATE AND CHALLENGE STATUS CHECKS
  const isDueDatePassed = moment().isAfter(
    moment(project?.challenge_details?.due_date)
  );
  const challengeStatusClosed =
    project?.challenge_details?.challenge_status === "closed";

  // STATE FOR ASSESSMENT MODAL VISIBILITY
  const [assessmentModelOpen, setAssessmentModelOpen] = useState(false);

  // CUSTOM HOOKS FOR PROJECT OPERATIONS AND REDIRECTS
  const {
    deleteMedia,
    getHistory,
    deleteProject,
    submit,
    deleteAssessment,
    favUnfav,
    pinUnpin,
  } = useProjects();
  const { navigateProjects } = useRedirect();
  const { isLoggedIn } = useSelector((state) => state[REDUCER_TYPES.AUTH]);

  // FETCH HISTORY WHEN TAB 4 IS ACTIVE AND NOT YET LOADED
  useEffect(() => {
    if (!isLoggedIn || tab !== 4 || Boolean(history)) return;
    fetchHistory();
  }, [tab]);

  // FETCHES PROJECT HISTORY DATA
  const fetchHistory = async () => {
    setHistory("loading");
    const res = await getHistory(project?.slug);
    setHistory(res);
  };

  // HANDLES DELETION OF THE ENTIRE PROJECT
  const handleDeleteProject = async () => {
    await deleteProject(project?.slug);
    setOpenConfirmationModal(false);
    navigateProjects();
  };

  // HANDLES PROJECT SUBMISSION WITH OPTIONAL LATE MESSAGE
  const handleSubmit = async () => {
    const res = await submit(project?.slug, {
      late_submission_msg: lateSubmissionMessage,
    });
    if (res) setProject(res);
    return res;
  };

  // HANDLES DELETION OF MEDIA BY TYPE AND UPDATES STATE LOCALLY
  const handleDeleteMedia = (id, type) => {
    setProject((prevProject) => {
      const updatedProject = { ...prevProject };
      let mediaKey;
      switch (type) {
        case "audio":
          mediaKey = "audios";
          break;
        case "video":
          mediaKey = "videos";
          break;
        case "docs":
          mediaKey = "docs";
          break;
        case "image":
          mediaKey = "images";
          break;
        default:
          return prevProject;
      }
      if (mediaKey && updatedProject[mediaKey]) {
        updatedProject[mediaKey] = updatedProject[mediaKey].filter(
          (item) => item.id !== id
        );
      }
      deleteMedia(project?.slug, id, type);
      return updatedProject;
    });
  };

  // HANDLES DELETION OF ASSESSMENT AND RESETTING ITS STATUS TO DRAFT
  const handleDeleteAssessment = async () => {
    setLoading(true);
    await deleteAssessment(project?.slug);
    setProject((prevProject) => ({
      ...prevProject,
      assessment_data: {
        ...(prevProject?.assessment_data || {}),
        assessment_status: "draft",
      },
    }));
    setLoading(false);
  };

  // GENERIC HANDLER FOR FAVORITE/PIN TOGGLES BASED ON CURRENT STATE
  const doUndoAction = async (property, doAction, undoAction) => {
    const did =
      !!project[property] && project[property]?.toLowerCase() !== "no";
    let res;
    if (did) {
      res = await undoAction(project?.slug, true);
    } else {
      res = await doAction(project?.slug);
    }
    if (!res) return;
    project[property] = did ? "no" : "yes";
    setProject({ ...project });
  };

  // FAVORITE/UNFAVORITE TOGGLE HANDLER
  const handleFavoriteUnFav = () =>
    doUndoAction("favourite", favUnfav, favUnfav);

  // PIN/UNPIN TOGGLE HANDLER
  const handlePinUnpin = () =>
    doUndoAction("is_project_pinned", pinUnpin, pinUnpin);

  // TAB LABELS WITH CONDITIONAL ICON RENDERING FOR COMPLETED REQUIREMENTS
  const tabsWithIcons = useMemo(
    () =>
      project_tabs.map((tabName, index) => {
        if (
          isRequirementCompleted(
            project.requirement_status,
            getRequirementTypesWithTab(index)
          ) &&
          isLoggedIn
        ) {
          return (
            <div key={index} style={{ display: "flex", alignItems: "center" }}>
              <CheckCircle className="mr-2 c-success" />
              {tabName}
            </div>
          );
        }

        return tabName;
      }),
    [project]
  );
  return (
    <>
      <MainPageLayout
        classes="pt-header mt-8 mb-5"
        firstLgGridSize={4}
        secondLgGridSize={8}
      >
        <div className="left">
          <ImageCard
            image={project?.media}
            height="250px"
            isEmbedded={project?.media_type === "embedded"}
            type={COMPONENT_TYPES.PROJECT}
            title={project?.title}
          />
          <div className="flex my-2">
            {project?.project_role === "submitter" &&
            project?.joined_status !== "no" ? (
              project?.access_level !== "viewer" && (
                <>
                  <ProjectCTA
                    project={project}
                    setAssessmentModelOpen={setAssessmentModelOpen}
                    setSubmissionOpen={setSubmissionOpen}
                    isDueDatePassed={isDueDatePassed}
                    challengeStatusClosed={challengeStatusClosed}
                    t={t}
                  />
                  <ProjectManageOptions
                    data={project}
                    onDelete={setOpenConfirmationModal}
                    classes="w-40p h-40 ml-1 fs-15"
                    pinUnpin={handlePinUnpin}
                  />
                </>
              )
            ) : project?.project_role === "assessor" ? (
              project?.assessment_data?.assessment_status === "published" ? (
                <>
                  <Button
                    width="50%"
                    height="50px"
                    color={challengeStatusClosed ? "blue" : "orange"}
                    onClick={() => setAssessmentOpen(true)}
                  >
                    {challengeStatusClosed
                      ? t("View Assessment")
                      : t("Edit Assessment")}
                  </Button>
                  <Button
                    width="50%"
                    classes="ml-1"
                    height="50px"
                    loading={loading}
                    disabled={challengeStatusClosed}
                    color={challengeStatusClosed ? "gray" : "red"}
                    bordered
                    onClick={handleDeleteAssessment}
                  >
                    {t("Delete Assessment")}
                  </Button>
                </>
              ) : (
                <Button
                  width="100%"
                  height="50px"
                  color="orange"
                  onClick={() => setAssessmentOpen(true)}
                >
                  {t("Assess Project")}
                </Button>
              )
            ) : null}
          </div>
          <ProjectDescription
            project={project}
            onShare={setShareOpen}
            onLike={handleFavoriteUnFav}
          />
        </div>
        <div className="right">
          <MainTabs tabs={tabsWithIcons} current={tab} setCurrent={setTab} />
          {isLoggedIn ? (
            <>
              {tab === 0 && (
                <ProjectTabDescription
                  description={project?.description}
                  t={t}
                />
              )}
              {tab === 1 && (
                <ProjectOverview
                  project={project}
                  setProject={setProject}
                  pitchCompleted={isRequirementCompleted(
                    project?.requirement_status,
                    ["pitch"]
                  )}
                  taskCompleted={isRequirementCompleted(
                    project?.requirement_status,
                    ["task"]
                  )}
                  t={t}
                />
              )}
              {tab === 2 && (
                <ProjectFiles
                  project={project}
                  onDeleteMedia={handleDeleteMedia}
                  t={t}
                  setProject={setProject}
                />
              )}
              {tab === 3 && (
                <Card classes="my-5">
                  <Discussions
                    component={COMPONENT_TYPES.PROJECT}
                    slug={project?.slug}
                  />
                </Card>
              )}
              {tab === 4 && (
                <ProjectHistory
                  history={history}
                  loading={history === "loading"}
                  t={t}
                />
              )}
            </>
          ) : (
            <NotLoggedInTabs title="Login to view more details" />
          )}
        </div>
      </MainPageLayout>
      {assessmentOpen ? (
        <ProjectAssessModal
          open={Boolean(assessmentOpen)}
          onClose={() => setAssessmentOpen(false)}
          project={project}
          onSuccessCb={(res) =>
            setProject((prev) => ({ ...prev, assessment_data: res }))
          }
        />
      ) : null}
      {assessmentAIOpen ? (
        <ProjectAssessmentAIModal
          open={Boolean(assessmentAIOpen)}
          onClose={() => setAssessmentAIOpen(false)}
          project={project}
        />
      ) : null}
      <ProjectSubmissionModal
        open={Boolean(submissionOpen)}
        onClose={() => setSubmissionOpen(false)}
        project={project}
        submit={handleSubmit}
        lateSubmissionMessage={lateSubmissionMessage}
        setLateSubmissionMessage={setLateSubmissionMessage}
        isDueDatePassed={isDueDatePassed}
      />
      {assessmentModelOpen ? (
        <ProjectAssessmentDetailsModal
          open={Boolean(assessmentModelOpen)}
          onClose={() => setAssessmentModelOpen(false)}
          details={project}
          slug={project?.challenge_details?.slug}
        />
      ) : null}
      {shareOpen && (
        <ShareModal
          open={shareOpen}
          onClose={() => setShareOpen(false)}
          component={COMPONENT_TYPES.PROJECT}
          slug={project?.slug}
        />
      )}
      <ConfirmationModal
        open={Boolean(openConfirmationModal)}
        onClose={() => setOpenConfirmationModal(false)}
        desc={t("You will delete this Project permanently, are you sure?")}
        okayButtonTitle="Delete"
        onOkay={handleDeleteProject}
      />
    </>
  );
};

export default ProjectsMainPage;
